import {useState} from 'react';
import whatInput from 'what-input';

/**
 * Focus ring of the search input: 1px `#0F0F0F` / `clr-on-bg-high`, 2px offset, 4px radius
 * (the `rounded` utility sits on the input itself).
 *
 * The `focus-visible:` prefixes are kept on purpose: the ring may only ever paint while the input
 * really has the focus, the flag below narrows that down to keyboard focus.
 */
export const KEYBOARD_FOCUS_RING_CLASSES =
    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-clr-on-bg-high';

/**
 * Tells whether the focus currently sitting on an element arrived there by keyboard, so that a focus
 * ring can be shown for Tab but not for a mouse click.
 *
 * Needed for text fields: `:focus-visible` always matches an `<input type="text">`, even when it is
 * focused with the mouse, so the CSS pseudo-class alone cannot express "keyboard only".
 *
 * The input method is sampled once, when the focus enters, and then kept until it leaves again.
 * Reading it live is not an option, because `what-input` keeps updating while the field stays
 * focused: a plain mouse move switches `intent` to `mouse` (would drop the ring while typing) and
 * every keystroke switches `input` to `keyboard` (would raise the ring after a mouse click).
 *
 * `ask()` (last used input) is read instead of `ask('intent')`, because `what-input` skips the intent
 * update while a form element is focused — clicking straight from another text field into this one
 * would otherwise still count as keyboard.
 */
/**
 * Focus ring of the controls inside the search overlay (search tags, insert arrow), matching neebo:
 * 2px `clr-on-bg-high`, 2px offset, 4px radius.
 */
export const FOCUS_VISIBLE_RING_CLASSES =
    'rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clr-on-bg-high';

export const useKeyboardFocus = () => {
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);

    return {
        isKeyboardFocus,
        onFocusCapture: () => setIsKeyboardFocus(whatInput.ask() === 'keyboard'),
        onBlurCapture: () => setIsKeyboardFocus(false),
    };
};
